/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';

import { useState, useContext, useEffect } from 'react';
import { GlobalContext } from '../../api/GlobalContext';
import { gradualType } from '../../api/Common';

const welcomeMessagePrefix =
  'Welcome to Comprehend PT! Together we are changing the world of medical documentation with the help of AI! ';
const welcomeMessageLite = 'Welcome to the free trial.';
const welcomeMessageActive = 'Your Subscription is active and ready to go!';
const welcomeMessageExpired =
  'Your subscription is expired. Please renew to continue using Comprehend PT.';

const Header = ({ isLite, flush = false }) => {
  /*
        Description: 
            The header component for all user pages. 
            The full header includes:
                User's name
                Welcome message
                Cards for:
                    Minutes Transcribed, Conversations Had, Patients Seen, Minutes Saved
                Link to subscription page if necessary
            When only the lite header is needed, pass in isLite={true} as a prop
                Only the welcome message and link to subscription page will be displayed (If necessary)
        */

  const { apiClient } = useContext(GlobalContext);

  const [name, setName] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [minutesTranscribed, setMinutesTranscribed] = useState('...');
  const [conversationsHad, setConversationsHad] = useState('...');
  const [patientsSeen, setPatientsSeen] = useState('...');
  const [minutesSaved, setMinutesSaved] = useState('...');
  const [showSubscriptionLink, setShowSubscriptionLink] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [trialValue, setTrialValue] = useState(null);
  const [trialUnit, setTrialUnit] = useState('days');
  // Update the user profile the first time this component loads
  useEffect(() => {
    apiClient
      .updateProfile()
      .then((data) => {
        // Active Message
        let subStatus = isLite ? welcomeMessageLite : welcomeMessagePrefix;

        // console.log("Active:")
        if (apiClient.active && apiClient.subscription !== 'FREE_TRIAL') {
          subStatus += welcomeMessageActive;
        } else if (apiClient.actionAllowed()) {
          // Free Trial Message
          setShowSubscriptionLink(true);
          if (apiClient.subscription === 'FREE_TRIAL') {
            const expirationDate = new Date(apiClient.expiration);
            const diffMs = expirationDate - new Date();
            if (diffMs > 0) {
              if (diffMs < 24 * 60 * 60 * 1000) {
                setTrialValue(Math.ceil(diffMs / (1000 * 60 * 60)));
                setTrialUnit('hours');
              } else {
                setTrialValue(Math.floor(diffMs / (1000 * 60 * 60 * 24)));
                setTrialUnit('days');
              }
            }
          } else {
            subStatus += `You have ${apiClient.creditsRemaining} visits available!`;
          }
        } else {
          setShowSubscriptionLink(true);
          subStatus += welcomeMessageExpired;
        }

        // On successful profile update set the fields
        if (data) {
          // setName(apiClient.name);
          setMinutesTranscribed(apiClient.minutesTranscribed);
          setConversationsHad(apiClient.conversationsHad);
          setPatientsSeen(apiClient.patientsSeen);
          setMinutesSaved(apiClient.minutesSaved);

          setName(apiClient.name);
          setWelcomeMessage(subStatus);
          setShowWelcomeMessage(apiClient.subscription === 'FREE_TRIAL');
        } else {
          setName(apiClient.name);
          setWelcomeMessage(subStatus);
          setMinutesTranscribed(apiClient.minutesTranscribed);

          setConversationsHad(apiClient.conversationsHad);
          setPatientsSeen(apiClient.patientsSeen);
          setMinutesSaved(apiClient.minutesSaved);
          setShowWelcomeMessage(apiClient.subscription === 'FREE_TRIAL');
        }
        // if false, we already had the data
      })
      .catch((error) => {
        // probably no session key
        console.error(error);
      });
  }, [apiClient]);

  // flush (the visit view): below 768px (the side panel) no green band for
  // the page to sit on, only a slim strip when there's a trial notice to
  // show. From 768px up, the usual band, unchanged.
  const flushClasses = isLite && showWelcomeMessage ? 'py-1 pt-md-2 pb-md-8' : 'd-none d-md-block pb-8 pt-2';

  return (
    <>
      <div className={`header bg-gradient-green ${flush ? flushClasses : isLite ? 'pb-8 pt-2' : 'pb-4 pt-5 pt-md-8'}`}>
        <Container fluid>
          <div className="header-body">
            {isLite && showWelcomeMessage && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                {trialValue !== null ? (
                  <span className="text-white">
                    <span
                      className={`font-weight-bold ${trialUnit === 'hours' ? 'text-danger' : 'text-warning'}`}
                    >
                      {trialValue}
                    </span>{' '}
                    {trialUnit} left in trial
                  </span>
                ) : (
                  <span className="text-white">Your trial has expired</span>
                )}
                <Link
                  to="/User/account/billing"
                  style={{ marginLeft: '12px' }}
                >
                  <Button
                    color="secondary"
                    size="sm"
                    className="text-primary text-uppercase font-weight-bold"
                  >
                    Subscribe
                  </Button>
                </Link>
              </div>
            )}
            {!isLite && (
              <>
                {/* Free trial compact banner */}
                {showWelcomeMessage && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <span className="text-white">
                      {trialValue !== null ? (
                        <>
                          You have{' '}
                          <span
                            className={`font-weight-bold ${trialUnit === 'hours' ? 'text-danger' : 'text-warning'}`}
                          >
                            {trialValue}
                          </span>{' '}
                          {trialUnit} left in trial
                        </>
                      ) : (
                        'Your trial has expired'
                      )}
                    </span>
                    <Link
                      to="/User/account/billing"
                      style={{ marginLeft: '12px' }}
                    >
                      <Button
                        color="secondary"
                        size="sm"
                        className="text-primary text-uppercase font-weight-bold"
                      >
                        Subscribe
                      </Button>
                    </Link>
                  </div>
                )}
                {showSubscriptionLink && !showWelcomeMessage && (
                  <div style={{ marginBottom: '8px' }}>
                    <Link
                      to="/User/account/billing"
                    >
                      <Button
                        color="secondary"
                        size="sm"
                        className="text-primary text-uppercase font-weight-bold"
                      >
                        Subscribe
                      </Button>
                    </Link>
                  </div>
                )}
                {/* Welcome Message */}
                <Row>
                  <Col lg="7" md="10">
                    <h1 className="display-4 text-white mr-3">Hello {name}</h1>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </Container>
      </div>
    </>
  );
};

export default Header;
